import React, { useEffect, useState } from "react";
import { X, UserPlus, Check, Mic } from "lucide-react";
import Avatar from "./Avatar";
import VoiceInput from "./VoiceInput";
import { useClara } from "../contexts/ClaraContext";

const SEED_OPTIONS = [
    { id: "jenny", label: "Warm gold" },
    { id: "tom", label: "Forest green" },
    { id: "doctor", label: "Navy" },
    { id: "walgreens", label: "Crimson" },
    { id: "bookclub", label: "Plum" },
    { id: "default", label: "Default" },
];

const RELATIONS = [
    "Daughter",
    "Son",
    "Husband",
    "Wife",
    "Sister",
    "Brother",
    "Mother",
    "Father",
    "Friend",
    "Neighbor",
    "Caregiver",
    "Doctor",
];

const SUGGESTED_SEED = {
    Daughter: "jenny",
    Mother: "jenny",
    Sister: "jenny",
    Wife: "jenny",
    Son: "tom",
    Husband: "tom",
    Brother: "tom",
    Father: "tom",
    Doctor: "doctor",
    Caregiver: "doctor",
    Friend: "bookclub",
    Neighbor: "bookclub",
};

export const AddContactDialog = ({ open, onClose, onAdd, initialValues }) => {
    const { speak } = useClara();
    const [name, setName] = useState("");
    const [relation, setRelation] = useState("Daughter");
    const [seed, setSeed] = useState("jenny");
    const [voiceFilled, setVoiceFilled] = useState(false);
    const [relationAnnounced, setRelationAnnounced] = useState(false);

    // Reset whenever the dialog opens — pre-fills from initialValues if provided
    useEffect(() => {
        if (!open) return;
        const initRelation = initialValues?.relation || "Daughter";
        setName(initialValues?.name || "");
        setRelation(initRelation);
        setSeed(
            initialValues?.seed ||
                SUGGESTED_SEED[initRelation] ||
                "jenny",
        );
        setVoiceFilled(!!(initialValues?.name || initialValues?.relation));
    }, [open, initialValues]);

    if (!open) return null;

    const submit = (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        onAdd({ name: name.trim(), relation, avatarSeed: seed });
        onClose?.();
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Add trusted contact"
            data-testid="add-contact-dialog"
            className="fixed inset-0 z-[55] flex items-end sm:items-center justify-center p-4 bg-[#1A1A2E]/60 backdrop-blur-sm"
        >
            <form
                onSubmit={submit}
                className="w-full max-w-[448px] bg-white rounded-3xl shadow-2xl border border-[#0A3D7A]/15 p-6 fade-in-up"
            >
                <div className="flex items-center justify-between mb-1">
                    <h2 className="font-display text-2xl font-bold text-[#1A1A2E]">
                        Add to Trusted Circle
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close add contact dialog"
                        data-testid="add-contact-close-btn"
                        className="w-10 h-10 rounded-xl text-[#5A5A6E] hover:bg-[#FFF8F0] flex items-center justify-center"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                {voiceFilled && (
                    <p
                        data-testid="add-contact-voice-pill"
                        className="mb-3 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#0A5C36] bg-[#E5F1EB] border border-[#0A5C36]/30 rounded-full px-3 py-1"
                    >
                        <Mic className="w-3.5 h-3.5" aria-hidden="true" />
                        Filled from your voice
                    </p>
                )}
                <p className="text-sm text-[#5A5A6E] mb-5">
                    They'll be able to receive your alerts and check in on you.
                </p>

                <div className="space-y-4">
                    <div>
                        <label
                            htmlFor="contact-name"
                            className="block text-xs uppercase tracking-wider font-semibold text-[#5A5A6E] mb-1.5"
                        >
                            Name
                        </label>
                        <VoiceInput
                            id="contact-name"
                            value={name}
                            onChange={setName}
                            fieldLabel="contact's first name"
                            placeholder="First name"
                            testId="add-contact-name-input"
                            inputClassName="w-full min-h-[52px] rounded-2xl border-2 border-[#0A3D7A]/15 pl-4 pr-14 text-base text-[#1A1A2E] focus:border-[#0A3D7A] focus:outline-none"
                            inputProps={{ required: true, autoComplete: "given-name" }}
                        />
                    </div>

                    <div>
                        <label
                            htmlFor="contact-relation"
                            className="block text-xs uppercase tracking-wider font-semibold text-[#5A5A6E] mb-1.5"
                        >
                            Relationship
                        </label>
                        <select
                            id="contact-relation"
                            value={relation}
                            onChange={(e) => {
                                const next = e.target.value;
                                setRelation(next);
                                setSeed(SUGGESTED_SEED[next] || "jenny");
                            }}
                            onFocus={() => {
                                if (relationAnnounced) return;
                                setRelationAnnounced(true);
                                speak(
                                    "Relationship dropdown. Use the up and down arrow keys to choose, then press tab.",
                                );
                            }}
                            onBlur={() => setRelationAnnounced(false)}
                            data-testid="add-contact-relation-input"
                            className="w-full min-h-[52px] rounded-2xl border-2 border-[#0A3D7A]/15 px-4 text-base text-[#1A1A2E] focus:border-[#0A3D7A] focus:outline-none bg-white"
                        >
                            {RELATIONS.map((r) => (
                                <option key={r} value={r}>
                                    {r}
                                </option>
                            ))}
                        </select>
                    </div>

                    <fieldset>
                        <legend className="block text-xs uppercase tracking-wider font-semibold text-[#5A5A6E] mb-2">
                            Avatar color
                        </legend>
                        <div className="grid grid-cols-3 gap-2">
                            {SEED_OPTIONS.map((opt) => (
                                <button
                                    key={opt.id}
                                    type="button"
                                    onClick={() => setSeed(opt.id)}
                                    aria-pressed={seed === opt.id}
                                    aria-label={`Avatar style: ${opt.label}`}
                                    data-testid={`add-contact-seed-${opt.id}`}
                                    className={`relative flex items-center justify-center p-2 rounded-2xl border-2 transition-colors ${
                                        seed === opt.id
                                            ? "border-[#0A3D7A] bg-[#FFF8F0]"
                                            : "border-[#0A3D7A]/15 hover:border-[#0A3D7A]/40"
                                    }`}
                                >
                                    <Avatar
                                        seed={opt.id}
                                        name={name || opt.label}
                                        size={40}
                                        ring={false}
                                    />
                                    {seed === opt.id && (
                                        <span
                                            aria-hidden="true"
                                            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#0A5C36] text-white flex items-center justify-center"
                                        >
                                            <Check className="w-3 h-3" />
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </fieldset>
                </div>

                <div className="mt-6 space-y-2">
                    <button
                        type="submit"
                        data-testid="add-contact-submit-btn"
                        className="w-full min-h-[56px] rounded-2xl bg-[#0A3D7A] hover:bg-[#082F5E] text-white text-lg font-bold flex items-center justify-center gap-2 transition-colors shadow-sm"
                    >
                        <UserPlus className="w-5 h-5" aria-hidden="true" />
                        Add contact
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-full min-h-[48px] rounded-2xl border-2 border-[#0A3D7A]/20 text-[#5A5A6E] font-semibold hover:bg-[#FFF8F0] transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddContactDialog;
